/**
 * Top-level router.
 *
 * Public storefront (anyone — no login):
 *   /                    → LandingPage
 *   /buy/:reportSlug     → ProductPage (birth-data form + coupon)
 *   /checkout/:orderId   → CheckoutPage (Razorpay modal trigger)
 *   /thank-you/:token    → ThankYouPage
 *   /order/:token        → OrderStatusPage
 *
 * Private workbench (existing institute tool):
 *   /workbench           → MenuPage
 *   /workbench/r/:key    → individual report page
 *   /workbench/admin/*   → professor-only commerce admin
 *
 * The private subtree is gated by <RequireAuth />; everything else is
 * open. There is no global state-machine router anymore.
 */
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/useAuth';
import { LangProvider } from './i18n/useLang';
import { RequireAuth, RequireRole } from './auth/RouteGuards';
import { WorkbenchShell } from './pages/WorkbenchShell';
import { CouponsAdminPage } from './pages/admin/CouponsAdminPage';
import { OrdersAdminPage } from './pages/admin/OrdersAdminPage';
import { CheckoutPage } from './pages/storefront/CheckoutPage';
import { LandingPage } from './pages/storefront/LandingPage';
import { OrderStatusPage } from './pages/storefront/OrderStatusPage';
import { ProductPage } from './pages/storefront/ProductPage';
import { ThankYouPage } from './pages/storefront/ThankYouPage';

export function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public storefront */}
            <Route element={<StorefrontShell />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/buy/:reportSlug" element={<ProductPage />} />
              <Route path="/checkout/:orderId" element={<CheckoutPage />} />
              <Route path="/thank-you/:token" element={<ThankYouPage />} />
              <Route path="/order/:token" element={<OrderStatusPage />} />
            </Route>

            {/* Private workbench — anything under /workbench needs login */}
            <Route
              path="/workbench/*"
              element={
                <RequireAuth>
                  <WorkbenchShell />
                </RequireAuth>
              }
            />

            {/* Admin storefront — professor only */}
            <Route
              element={
                <RequireAuth>
                  <RequireRole role="professor">
                    <Outlet />
                  </RequireRole>
                </RequireAuth>
              }
            >
              <Route path="/workbench/admin/coupons" element={<CouponsAdminPage />} />
              <Route path="/workbench/admin/orders" element={<OrdersAdminPage />} />
            </Route>

            {/* Anything else — bounce to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}

function StorefrontShell() {
  return (
    <div className="min-h-screen">
      <Outlet />
    </div>
  );
}
